/**
 *
 * ErrorBoundary
 *
 * Card 38. React 16 unmounts the entire tree when a render throws, and this app
 * had no boundary anywhere, so any such throw left the user on a blank page with
 * no navigation and no way to reach the sign-in screen. The known cause was a
 * user record that no longer resolves (see the auth changes in the backend and
 * the profile guard in components/Routes), but the floor matters more than the
 * individual cause: whatever fails, the user is still offered a way back in.
 *
 */

import { Box, Button, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import Logo from '../Nav/TopNav/Logo';

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  logo: {
    margin: theme.spacing(2)
  },
  message: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  action: {
    marginTop: theme.spacing(4)
  }
}));

/**
 * Drop the session cookie before leaving, so a session the server can no longer
 * resolve does not follow the user back to the sign-in page. Mirrors the
 * clean-up in components/Routes' logoutAndChangeRegion.
 */
function signInAgain() {
  document.cookie = 'connect.sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  window.location.href = '/signin';
}

export function ErrorScreen() {
  const classes = useStyles();

  return (
    <Container component="main" maxWidth="xs">
      <div className={classes.paper}>
        <div className={classes.logo}>
          <Logo />
        </div>
        <Typography component="h1" variant="h6">
          Something went wrong
        </Typography>
        <Typography variant="body2" className={classes.message}>
          We could not load your account. Signing in again usually fixes it.
        </Typography>
        <Box width="100%" className={classes.action}>
          <Button fullWidth variant="contained" color="primary" onClick={signInAgain}>
            Sign in again
          </Button>
        </Box>
      </div>
    </Container>
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // No Sentry in this app yet, so the console is the only record we get.
    console.error('Unhandled render error', error, info);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorScreen />;
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node
};
